import {
  AxiosRequestHeaders,
  AxiosError,
  AxiosResponse,
  Method,
  ResponseType,
} from 'axios';
import { prepUrlParams, prepUrlQueryParams, QueryParam } from './http';
import axiosInstance from './axiosInstance';
import { useEffect, useState } from 'react';

// Extend the RequestType to include a retry property.
export interface RequestType {
  method: Method;
  url: string;
  timeout?: number;
  payload?: unknown;
  responseType?: ResponseType;
  headers?: AxiosRequestHeaders;
  isPublic?: boolean;
  retry?: number; // Specify how many retries should occur on failure.
}

export function apiCall<T>(request: RequestType, token?: string): Promise<T> {
  const isPrivate = request.isPublic === false;
  return new Promise((resolve, reject) => {
    axiosInstance({
      method: request.method,
      url: request.url,
      timeout: request.timeout,
      data: request.payload,
      ...(request.responseType && { responseType: request.responseType }),
      headers: {
        ...(token && isPrivate && { Authorization: `Bearer ${token}` }),
        ...request.headers,
      } as AxiosRequestHeaders,
    })
      .then((response: AxiosResponse<T>) => resolve(response.data))
      .catch((error: AxiosError) => reject(error));
  });
}

export type FetcherProps = RequestType & {
  urlParams?: Array<QueryParam>;
  urlQueryParams?: Array<QueryParam>;
  payloadResolver?: (values: any) => any;
};

export default function fetcher<T>(request: FetcherProps) {
  // Dynamic token state to handle token updates.
  const [token, setToken] = useState(localStorage.getItem('accessToken'));

  useEffect(() => {
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === 'accessToken') {
        setToken(localStorage.getItem('accessToken'));
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  return (payload: unknown): Promise<T> => {
    const isPrivate = request.isPublic === false;
    if (isPrivate && !token) {
      return Promise.reject(new Error('Token is null or undefined'));
    }

    const resolvedPayload = request.payloadResolver
      ? request.payloadResolver(payload)
      : payload;

    // Prepare the URL with URL params or query params if provided.
    const url =
      request.urlParams || request.urlQueryParams
        ? `${prepUrlParams(request.url, request.urlParams || [])}${prepUrlQueryParams(
            request.urlQueryParams || []
          )}`
        : request.url;

    const finalRequest: RequestType = {
      ...request,
      payload: resolvedPayload,
      url,
    };

    // Use the retry value provided in the request config.
    // For example, if retry is set to 0 (like in your GET_AIRPORTS config),
    // the API call will be attempted only once.
    const maxRetries = typeof request.retry === 'number' ? request.retry : 3;

    const attemptApiCall = (attempt: number = 0): Promise<T> => {
      return apiCall<T>(finalRequest, isPrivate ? token ?? undefined : undefined)
        .catch((error: AxiosError) => {
          if (attempt < maxRetries) {
            // Optionally, you can add a delay here before retrying.
            return attemptApiCall(attempt + 1);
          } else {
            // After maxRetries, reject with the last error.
            return Promise.reject(error);
          }
        });
    };

    // Initiate the API call with retry logic.
    return attemptApiCall();
  };
}
